import { useMemo } from "react";
import {
  useClientConfiguration,
  useClientDetailTabColumnConfiguration,
} from "../../data/global/useProductConfiguration";
import { PlanFeed } from "../../generate";

export interface ColumnVisibilityResult {
  visibleColumns: (keyof PlanFeed)[];
  isColumnVisible: (columnKey: keyof PlanFeed) => boolean;
}

/**
 * Centralized hook to determine which columns should be visible
 * based on client configuration and indication
 */
export const usePayerLandscapeColumnVisibility = (
  client?: string | null,
  indication?: string | null | undefined,
  role?: string | null,
): ColumnVisibilityResult => {
  const config = useClientConfiguration(client);
  const defaultConfig = useClientDetailTabColumnConfiguration(client);

  const visibleColumns = useMemo((): (keyof PlanFeed)[] => {
    if (!client) {
      return defaultConfig.dataKeys || [];
    }

    let baseColumns: (keyof PlanFeed)[] = defaultConfig.dataKeys || [];

    // Check if indication filtering is enabled for this client
    const indicationFiltering = config?.payerLandscape?.indicationFiltering;

    if (indicationFiltering && indication) {
      // Try to get indication-specific columns
      const indicationMappings = config?.payerLandscape?.indicationColumnMappings;
      if (indicationMappings && indicationMappings[indication]) {
        baseColumns = indicationMappings[indication];
      }
    }

    if (role) {
      const roleAdditionalColumns = config?.payerLandscape?.roleAdditionalColumns?.[role];
      if (roleAdditionalColumns && roleAdditionalColumns.length > 0) {
        return [...baseColumns, ...roleAdditionalColumns];
      }
    }

    return baseColumns as (keyof PlanFeed)[];
  }, [client, indication, role, config, defaultConfig]);

  const isColumnVisible = useMemo(() => {
    return (columnKey: keyof PlanFeed) => visibleColumns.includes(columnKey);
  }, [visibleColumns]);

  return {
    visibleColumns,
    isColumnVisible,
  };
};

// Define the filter mapping type
type FilterColumnMapping = {
  relativeStatus: keyof PlanFeed;
  monthlyChange: keyof PlanFeed;
  tclMarketShare: keyof PlanFeed;
  productTrx: keyof PlanFeed;
  marketTrx: keyof PlanFeed;
  coverageStatus: keyof PlanFeed;
  competitorCoverageStatus: keyof PlanFeed;
  competitorRelativeStatus: keyof PlanFeed;
  competitorProductTrx: keyof PlanFeed;
};

export interface FilterVisibilityResult {
  isFilterVisible: (filterKey: keyof FilterColumnMapping) => boolean;
  filterColumnMapping: FilterColumnMapping;
}

/**
 * Hook specifically for filter visibility in modals
 * Maps filter fields to their corresponding column keys
 */
export const usePayerLandscapeFilterVisibility = (
  client?: string | null,
  indication?: string | null | undefined,
): FilterVisibilityResult => {
  const { isColumnVisible } = usePayerLandscapeColumnVisibility(client, indication);

  // Mapping of filter fields to their corresponding column keys
  const filterColumnMapping: FilterColumnMapping = useMemo(
    () => ({
      relativeStatus: "rel_status",
      monthlyChange: "share_delta",
      tclMarketShare: "mkt_share",
      productTrx: "prod_trx",
      marketTrx: "mkt_trx",
      coverageStatus: "cc_status",
      competitorCoverageStatus: "comp_cc_status",
      competitorRelativeStatus: "comp_rel_status",
      competitorProductTrx: "comp_prod_trx",
    }),
    [],
  );

  const isFilterVisible = useMemo(() => {
    return (filterKey: keyof FilterColumnMapping) => {
      const columnKey = filterColumnMapping[filterKey];
      return isColumnVisible(columnKey);
    };
  }, [isColumnVisible, filterColumnMapping]);

  return {
    isFilterVisible,
    filterColumnMapping,
  };
};
